<template>
	<div class="ventas_tab report_theme_container" data-theme="ventas">
		<TabHeader
			title="Ventas"
			description="Analiza la tendencia y composición de tus ventas"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Summary Cards Section -->
		<SalesSummaryCards class="mt-5"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow" />

		<!-- Main Table Section -->
		<SalesEvolutionTable class="mt-6"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			@change-period="setPeriodRange"
			@retry="fetchSalesData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import SalesSummaryCards from "@/components/financialReports/SalesSummaryCards.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import { PERIOD_TYPES, FILTER_PERIOD_OPTIONS } from "../config/reportStructure";
import { capitalize } from "@/utils/string";

export default {
	name: "VentasTab",

	components: {
		TabHeader,
		SalesSummaryCards,
		SalesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("classification", ["categories"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("salesReport", [
			"activeSubFilter",
			"monthHeaders",
			"isLoading",
			"error",
		]),
		...mapGetters("salesReport", ["tableRows", "totalRow"]),

		getNow() {
			return moment().toDate();
		},

		subNavFilters() {
			const filters = [{ id: "cuentas", label: "Cuentas" }];
			this.categories.forEach(category => {
				filters.push({ id: category, label: capitalize(category) });
			});
			filters.push({ id: "clientes", label: "Clientes" });
			return filters;
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción.
		fetchSignature() {
			return [this.endMonth, this.selectedPeriodRange, this.activeSubFilter, this.filterPeriod].join("|");
		},

		// Las summary cards solo dependen del mes de referencia: refetch
		// independiente para no perderlas al cambiar period range o subfilter.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("salesReport", ["fetchSalesData", "fetchSalesSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("salesReport", ["SET_ACTIVE_SUB_FILTER"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateActiveSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
	},

	mounted() {
		this.fetchSalesData();
		this.fetchSalesSummary();
	},

	watch: {
		fetchSignature() { this.fetchSalesData(); },
		summarySignature() { this.fetchSalesSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS de los
// sub-filters dinámicos (Cuentas/Categorías/Clientes) inyectados vía
// el slot #center-sub-nav — reusan las classes .sub_nav_filters/.filter_btn
// que TabHeader expone con scoped styles propios, así que los repetimos
// localmente para que apliquen al markup del slot.

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}
</style>
